import React, { useContext, useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import {
  DELIVERY_METHOD_OPTIONS,
  DELIVERY_SPEED_OPTIONS,
  DeliveryType,
  isDeliveryType,
} from "../models/DeliveryType";
import ReadOnlyTextField from "./rfqs/ReadOnlyTextField";
import { AppContext } from "../AppContextProvider";

/** @deprecated Use catalog deliverySpeeds — kept for export dialog shipping autocomplete. */
export const QB_SHIPPING_METHODS = DELIVERY_SPEED_OPTIONS;

const SHIPPING_METHOD_DISPLAY_LABELS: Readonly<Record<string, string>> = {
  [DeliveryType.CustPickup]: "Client Pickup",
  [DeliveryType.FreightPriorit]: "FedEx Freight Priority",
  [DeliveryType.FreightEconomy]: "FedEx Freight Economy",
};

const SHIPPING_DISPLAY_TO_QB = Object.fromEntries(
  Object.entries(SHIPPING_METHOD_DISPLAY_LABELS).map(([qb, display]) => [
    display,
    qb,
  ])
) as Record<string, string>;

const LEGACY_SHIPPING_ALIASES: Readonly<Record<string, string>> = {
  "3rd day air": DeliveryType.ThirdDayAir,
  "2 Day": DeliveryType.SecondDayAir,
  "3 Day": DeliveryType.ThirdDayAir,
  "Overnight AM": DeliveryType.OvernightByAM,
  "Priority Freigh": DeliveryType.FreightPriorit,
  "Pick up": DeliveryType.PickUpByRCS,
  "Customer Pickup": DeliveryType.CustPickup,
  "Client Pickup": DeliveryType.CustPickup,
  "Freight Priority": DeliveryType.FreightPriorit,
  "FedEx Freight Priority": DeliveryType.FreightPriorit,
  "FedEx Freight Economy": DeliveryType.FreightEconomy,
  "Fed Ex Freight Priority": DeliveryType.FreightPriorit,
  "Fed-ex Freight Economy": DeliveryType.FreightEconomy,
  NextDay830: DeliveryType.OvernightByAM,
  NextDay1030: DeliveryType.OvernightBy1030,
  NextDayStandard: DeliveryType.Overnight,
  EndOfNextDay: DeliveryType.Overnight,
  TwoDay: DeliveryType.SecondDayAir,
  ThreeDay: DeliveryType.ThirdDayAir,
  Freight: DeliveryType.FreightEconomy,
  ClientPickup: DeliveryType.CustPickup,
  EmployeePickup: DeliveryType.PickUpByRCS,
  VendorDelivery: DeliveryType.VendorDeliver,
};

const LEGACY_SHIPPING_ALIAS_KEYS_LOWER = Object.fromEntries(
  Object.entries(LEGACY_SHIPPING_ALIASES).map(([k, v]) => [k.toLowerCase(), v])
);

export const OVERNIGHT_SHIPPING_METHODS: readonly string[] = [
  DeliveryType.Overnight,
  DeliveryType.OvernightBy1030,
  DeliveryType.OvernightByAM,
];

export const FREIGHT_SHIPPING_METHODS: readonly string[] = [
  DeliveryType.FreightAllowed,
  DeliveryType.FreightEconomy,
  DeliveryType.FreightPriorit,
];

export const DEFAULT_DELIVERY_SPEED = DeliveryType.Ground;

export function shippingMethodDisplayLabel(
  qbValue: DeliveryType | string,
  catalogLabelByValue?: Map<string, string>
): string {
  const trimmed = String(qbValue ?? "").trim();
  if (!trimmed) return "";
  const fromCatalog = catalogLabelByValue?.get(trimmed);
  if (fromCatalog) return fromCatalog;
  return SHIPPING_METHOD_DISPLAY_LABELS[trimmed] ?? trimmed;
}

export function normalizeLegacyShippingMethod(value: string): string {
  const trimmed = (value || "").trim();
  if (!trimmed) return DEFAULT_DELIVERY_SPEED;
  if (isDeliveryType(trimmed)) return trimmed;
  const legacy = LEGACY_SHIPPING_ALIASES[trimmed];
  if (legacy) return legacy;
  const legacyLower = LEGACY_SHIPPING_ALIAS_KEYS_LOWER[trimmed.toLowerCase()];
  if (legacyLower) return legacyLower;
  // Preserve admin-added / custom catalog values for QB.
  return trimmed;
}

/** Resolve UI or legacy text to the stored ShipMethod / speed string. */
export function shippingMethodToQuickBooks(
  value: DeliveryType | string
): string {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return DEFAULT_DELIVERY_SPEED;
  if (isDeliveryType(trimmed)) return trimmed;
  const fromDisplay = SHIPPING_DISPLAY_TO_QB[trimmed];
  if (fromDisplay) return fromDisplay;
  return normalizeLegacyShippingMethod(trimmed);
}

export function isOvernightShippingMethod(
  method: DeliveryType | string
): boolean {
  return OVERNIGHT_SHIPPING_METHODS.includes(shippingMethodToQuickBooks(method));
}

export function isFreightShippingMethod(
  method: DeliveryType | string
): boolean {
  return FREIGHT_SHIPPING_METHODS.includes(shippingMethodToQuickBooks(method));
}

export function isDirectDeliveryShippingMethod(
  method: DeliveryType | string | null | undefined
): boolean {
  return shippingMethodToQuickBooks(String(method ?? "")) === DeliveryType.DirectDelivery;
}

export function normalizeStoredDeliverySpeed(
  value: DeliveryType | string | null | undefined
): string {
  return shippingMethodToQuickBooks(String(value ?? ""));
}

function normalizeVendorShippingMethodsRecord(
  methods: Record<string, string>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [vendor, method] of Object.entries(methods)) {
    const qb = shippingMethodToQuickBooks(method);
    if (qb) out[vendor] = qb;
  }
  return out;
}

export { normalizeVendorShippingMethodsRecord };

interface DeliverySpeedAutocompleteProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  disableOvernightOptions?: boolean;
  freightOnly?: boolean;
  sx?: object;
}

/** Delivery Speed autocomplete (QB shipVia source). */
const DeliveryTypeAutocomplete: React.FC<DeliverySpeedAutocompleteProps> = ({
  value,
  onChange,
  label = "Delivery Speed",
  required = false,
  disabled = false,
  readOnly = false,
  disableOvernightOptions = false,
  freightOnly = false,
  sx = {},
}) => {
  const { deliverySpeeds } = useContext(AppContext);
  const catalogOptions = useMemo(() => {
    if (deliverySpeeds.length > 0) {
      return deliverySpeeds
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder || a.value.localeCompare(b.value))
        .map((t) => t.value);
    }
    return [...DELIVERY_SPEED_OPTIONS];
  }, [deliverySpeeds]);

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of deliverySpeeds) {
      if (t.label?.trim()) map.set(t.value, t.label.trim());
    }
    return map;
  }, [deliverySpeeds]);

  const qbValue = value ? normalizeStoredDeliverySpeed(value) : null;
  const options =
    qbValue && !catalogOptions.includes(qbValue)
      ? [...catalogOptions, qbValue]
      : catalogOptions;

  return readOnly ? (
    <ReadOnlyTextField
      fullWidth
      label={label}
      value={qbValue ? shippingMethodDisplayLabel(qbValue, labelByValue) : ""}
      variant="standard"
      sx={sx}
    />
  ) : (
    <Autocomplete<string>
      options={options}
      autoHighlight
      value={qbValue}
      onChange={(_, newValue) => onChange(newValue ?? null)}
      disabled={disabled}
      getOptionLabel={(opt) => shippingMethodDisplayLabel(opt, labelByValue)}
      getOptionDisabled={(option) => {
        const isFreight = FREIGHT_SHIPPING_METHODS.includes(option);
        if (freightOnly) {
          if (!isFreight) return true;
        } else if (isFreight) {
          return true;
        }
        if (
          disableOvernightOptions &&
          OVERNIGHT_SHIPPING_METHODS.includes(option)
        ) {
          return true;
        }
        return false;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="standard"
          required={required}
        />
      )}
      isOptionEqualToValue={(option, val) => option === val}
      sx={sx}
    />
  );
};

interface DeliveryMethodAutocompleteProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  allowDirectDelivery?: boolean;
  sx?: object;
}

export const DeliveryMethodAutocomplete: React.FC<
  DeliveryMethodAutocompleteProps
> = ({
  value,
  onChange,
  label = "Delivery Method",
  required = false,
  disabled = false,
  readOnly = false,
  allowDirectDelivery = false,
  sx = {},
}) => {
  const { deliveryMethods } = useContext(AppContext);
  const catalogOptions = useMemo(() => {
    if (deliveryMethods.length > 0) {
      return deliveryMethods
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder || a.value.localeCompare(b.value))
        .map((t) => t.value);
    }
    return [...DELIVERY_METHOD_OPTIONS];
  }, [deliveryMethods]);

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of deliveryMethods) {
      if (t.label?.trim()) map.set(t.value, t.label.trim());
    }
    return map;
  }, [deliveryMethods]);

  const normalized = value ? String(value).trim() || null : null;
  const options =
    normalized && !catalogOptions.includes(normalized)
      ? [...catalogOptions, normalized]
      : catalogOptions;

  return readOnly ? (
    <ReadOnlyTextField
      fullWidth
      label={label}
      value={
        normalized ? shippingMethodDisplayLabel(normalized, labelByValue) : ""
      }
      variant="standard"
      sx={sx}
    />
  ) : (
    <Autocomplete<string>
      options={options}
      autoHighlight
      value={normalized}
      onChange={(_, newValue) => onChange(newValue ?? null)}
      disabled={disabled}
      getOptionLabel={(opt) => shippingMethodDisplayLabel(opt, labelByValue)}
      getOptionDisabled={(option) => {
        if (
          !allowDirectDelivery &&
          option === DeliveryType.DirectDelivery
        ) {
          return true;
        }
        return false;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="standard"
          required={required}
        />
      )}
      isOptionEqualToValue={(option, val) => option === val}
      sx={sx}
    />
  );
};

export default DeliveryTypeAutocomplete;
